import CardStateHelper from '../CardStateHelper';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { ICardModifierData } from 'Prototype/Level/Card/ICardModifierData';
import CardStateModifierHelper from '../CardStateModifierHelper';
import { IGameState } from '../../Game/IGameState';
import { ICardsState, ICardStateData, ICardStateTaskData, JokerPreBoosterCardData } from '../ICardState';
import { initialCardStateData } from '../initialCardStateData';
import { CardState } from 'Prototype/Level/Card/CardState';
import { ModifierScoreMap } from 'Prototype/Level/Card/CardScoreMap';
import BaseModifierBehavior from '../ModifierBehavior/BaseModifierBehavior';
import { levelCoinCollectMultiplier } from 'Constants/Economy';
import { CardScoreMap } from 'Prototype/Level/Card/ModifierScoreMap';
import LevelStateHelper from 'State/Level/LevelStateHelper';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import LevelHelper from 'Utility/LevelHelper';
import { RewardType } from 'Utility/RewardHelper';
import { CardTask } from '../CardTasks';
import * as SAT from 'sat';
import { CardType } from 'Prototype/Level/Card/CardType';
import { ModifierTypeMap } from 'Prototype/Level/Card/ModifierTypeMap';

interface ICollectCardOptions {
    pushToUndoHistory?: boolean;
    forceClearUndoHistory?: boolean;
    incrementStreak?: boolean;
    addToTurnScore?: boolean;
    foldCard?: boolean;
    rewardCoins?: boolean;
    queueMatchTask?: boolean;
    cardScore?: number;
    updateModifiers?: boolean;
}

export default class BaseCardBehavior {
    public cardID: string;

    constructor(cardID: string) {
        this.cardID = cardID;
    }

    public isFaceUp(state: ICardsState): boolean {
        return state.cards[this.cardID].faceUp;
    }

    public isPlayable(state: ICardsState): boolean {
        const cardState = CardStateHelper.getCardState(state, this.cardID);
        switch (cardState) {
            case CardState.IN_PLAY:
                // Must be face-up and not covered by another card.
                return this.isFaceUp(state) && !this.isObscured(state);
            case CardState.IN_STACK:
                // Must be top of "stack".
                return this.cardID === state.inStack[state.inStack.length - 1];
            case CardState.FOLDED:
            case CardState.DISCARDED:
            case CardState.UNKNOWN:
                return false;
        }

        return true;
    }

    public isMatch(state: ICardsState, cardB: string): boolean {
        return true;
    }

    public play(state: IGameState): IGameState {
        return state;
    }

    /**
     * Called when a 'valid' turn is completed.
     */
    public update(state: ICardsState): ICardsState {
        return state;
    }

    /**
     * Check if cardA is above cardB and cardA's bounding box is intersecting cardB's bounding box.
     * @param state
     * @param card The target card to compare.
     */
    public isOverlapping(state: ICardsState, targetCardID: string) {
        // Cannot overlap itself.
        if (this.cardID === targetCardID) return false;

        const cardDataA: ICardStateData = (state.cards[this.cardID] as JokerPreBoosterCardData).hitArea || state.cards[this.cardID];
        const cardDataB: ICardStateData = (state.cards[targetCardID] as JokerPreBoosterCardData).hitArea || state.cards[targetCardID];
        const offsetA = cardDataA.offset ?? initialCardStateData.offset.clone();
        const offsetB = cardDataB.offset ?? initialCardStateData.offset.clone();

        // Target card must be below this card.
        let deltaDepth: number = cardDataA.depth - cardDataB.depth;
        if (deltaDepth <= 0) return false;

        const a = new SAT.Polygon(new SAT.Vector(0, 0), [
            new SAT.Vector(-cardDataA.width * 0.5, -cardDataA.height * 0.5),
            new SAT.Vector(cardDataA.width * 0.5, -cardDataA.height * 0.5),
            new SAT.Vector(cardDataA.width * 0.5, cardDataA.height * 0.5),
            new SAT.Vector(-cardDataA.width * 0.5, cardDataA.height * 0.5),
        ]);
        a.rotate(cardDataA.rotation);
        a.setOffset(new SAT.Vector(cardDataA.x + offsetA.x, cardDataA.y + offsetA.y));

        const b = new SAT.Polygon(new SAT.Vector(0, 0), [
            new SAT.Vector(-cardDataB.width * 0.5, -cardDataB.height * 0.5),
            new SAT.Vector(cardDataB.width * 0.5, -cardDataB.height * 0.5),
            new SAT.Vector(cardDataB.width * 0.5, cardDataB.height * 0.5),
            new SAT.Vector(-cardDataB.width * 0.5, cardDataB.height * 0.5),
        ]);
        b.rotate(cardDataB.rotation);
        b.setOffset(new SAT.Vector(cardDataB.x + offsetB.x, cardDataB.y + offsetB.y));

        return SAT.testPolygonPolygon(a, b);
    }

    /**
     * Return true if the specified card is covered/overlapped by other in-play cards.
     */
    public isObscured(state: ICardsState): boolean {
        for (let otherCardID of state.inPlay) {
            const behavior = CardStateHelper.getCardBehavior(
                state,
                otherCardID
            );
            if (behavior.isOverlapping(state, this.cardID)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Return true if card is face down or card is obscured or any of this card's modifiers will block this card from being played.
     */
    public isBlocked(
        state: ICardsState,
        exceptions?: CardModifierType[]
    ): boolean {
        return (
            !this.isFaceUp(state) ||
            this.isObscured(state) ||
            this.modifiersBlocking(state, exceptions)
        );
    }

    /**
     * Check if any of this card's modifiers will block this card from being played.
     */
    protected modifiersBlocking(
        state: ICardsState,
        exceptions?: CardModifierType[]
    ): boolean {
        return state.cards[this.cardID].modifiers.some(
            (modifier: ICardModifierData) => {
                if (exceptions && exceptions.includes(modifier.type)) {
                    return false;
                }
                const behavior = CardStateModifierHelper.getModifierBehavior(
                    state,
                    modifier,
                    this.cardID
                );
                return behavior.isBlocking(state);
            }
        );
    }

    public isBlockedByModifier(
        state: ICardsState,
        modifierType: CardModifierType
    ): boolean {
        const modifier = state.cards[this.cardID].modifiers.find(m => m.type === modifierType);

        let modifierIsBlocking = false;
        if (modifier) {
            const modifierBehavior = CardStateModifierHelper.getModifierBehavior(state, modifier, this.cardID);
            modifierIsBlocking = modifierBehavior.isBlocking(state);
        }

        return modifierIsBlocking;
    }

    public getModifiersScore(state: ICardsState): number {
        return state.cards[this.cardID].modifiers.reduce((count: number, modifier) => {
            return count + ModifierScoreMap[modifier.type];
        }, 0);
    }

    public modifiersOverriding(cardsState: ICardsState, options?: {
        whitelistTypes?:CardModifierType[];
        blacklistTypes?:CardModifierType[];
    }): BaseModifierBehavior[] {
        const { whitelistTypes = [], blacklistTypes = [] } = options ?? {};
        let modifiers = cardsState.cards[this.cardID].modifiers;

        if (whitelistTypes.length > 0) {
            modifiers = modifiers.filter(modifier => whitelistTypes.includes(modifier.type))
        }

        return modifiers
            .filter(modifier => !blacklistTypes.includes(modifier.type))
            .map(m =>
                CardStateModifierHelper.getModifierBehavior(
                    cardsState,
                    m,
                    this.cardID
                )
            )
            .filter(b => b.overrideCardPlay(cardsState));
    }

    public collectCard(oldState: IGameState, options?: ICollectCardOptions): IGameState {
        let state = { ...oldState };
        let cardsState = { ...state.levelState.cardsState };
        const cardID = this.cardID;

        const {
            incrementStreak = true,
            addToTurnScore = true,
            foldCard = true,
            pushToUndoHistory = true,
            forceClearUndoHistory = false,
            rewardCoins = false,
            queueMatchTask = true,
            updateModifiers = true
        } = options ?? {};
        let cardScore = options?.cardScore ?? CardScoreMap[cardsState.cards[cardID].type]

        if (pushToUndoHistory && !forceClearUndoHistory) {
            pushUndoHistory();
        }

        if (forceClearUndoHistory) {
            clearUndoHistory();
        }

        if (incrementStreak) {
            handleStreak(this.getModifiersScore(cardsState));
        }

        if (addToTurnScore) {
            addTurnScore();
        }

        if (foldCard) {
            setCardFolded();
        }

        if (updateModifiers) {
            state = CardStateModifierHelper.updateModifiers(
                state,
                cardID
            );
        }

        cardsState = CardStateHelper.getCardsState(state);

        let coinsCollected: number = 0;
        let prevCoins: number = state.playerState.inventory.coins;
        if (rewardCoins && !LevelStateHelper.checkLevelCompleted(state)) {
            giveCoinReward();
        }

        if (queueMatchTask) {
            state = this.addMatchTaskToQueue(state, {
                cardScore: cardScore,
                coinsCollected,
                startValue: prevCoins,
                finalValue: prevCoins + coinsCollected,
            })
        }

        checkStreakBonus();

        cardsState = CardStateHelper.updateCards(cardsState);

        cardsState = CardStateModifierHelper.onCardPlayed(
            cardsState,
            this.cardID
        );

        return CardStateHelper.updateGameState(oldState, cardsState);

        function checkStreakBonus(): void {
            state = CardStateHelper.updateGameState(state, cardsState);
            cardsState = CardStateHelper.checkStreakBonus(state);
            state = CardStateHelper.updateGameState(state, cardsState);
        }

        function pushUndoHistory() {
            cardsState = CardStateHelper.getCardsState(state);

            cardsState = CardStateHelper.pushUndoHistory(
                cardsState,
                cardID,
                CardState.IN_PLAY
            );
            state = CardStateHelper.updateGameState(state, cardsState);
        }

        function clearUndoHistory() {
            cardsState = CardStateHelper.getCardsState(state);

            cardsState = CardStateHelper.clearUndoHistory(cardsState);

            state = CardStateHelper.updateGameState(state, cardsState);
        }

        function handleStreak(modifierScore: number) {
            cardsState = CardStateHelper.getCardsState(state);

            // If there is a "scoreable" modifier (bomb or fog),
            // increase streak sequence without increase the streak score.
            if (modifierScore > 0) {
                cardsState = CardStateHelper.increaseStreak(
                    cardsState,
                    0,
                    cardID,
                );
            } else {
                cardsState = CardStateHelper.increaseStreak(
                    cardsState,
                    cardScore,
                    cardID,
                );
                cardScore = cardsState.streakScore;
            }

            state = CardStateHelper.updateGameState(state, cardsState);
        }

        function addTurnScore() {
            cardsState = CardStateHelper.getCardsState(state);

            cardsState = CardStateHelper.addTurnScore(
                cardsState,
                cardScore
            );

            state = CardStateHelper.updateGameState(state, cardsState);
        }

        function setCardFolded() {
            cardsState = CardStateHelper.getCardsState(state);

            cardsState = CardStateHelper.setCardFolded(
                cardsState,
                cardID
            );

            state = CardStateHelper.updateGameState(state, cardsState);
        }

        function giveCoinReward() {
            const levelCost: number = LevelHelper.getLevelCost(
                state.levelState.levelNumber
            );
            const startValue: number = Math.max(
                Math.round(levelCost * levelCoinCollectMultiplier),
                1
            );

            coinsCollected = startValue + state.levelState.cardsState.matchSequence.length;

            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    coinsCollected:
                        state.levelState.coinsCollected +
                        coinsCollected,
                },
                playerState: InventoryStateHelper.giveReward(
                    state.playerState,
                    {
                        type: RewardType.COINS,
                        value: coinsCollected,
                    },
                    { queueTask: false, forceAnim: true }
                ),
            };
            cardsState = CardStateHelper.getCardsState(state);
        }
    }

    public getCardType(cardsState: ICardsState): CardType {
        return cardsState.cards[this.cardID].type;
    }

    public getFlipDelays(cardsState: ICardsState): Record<string, number> {
        const delayByCardID: Record<string, number> = {};
        const cardState = cardsState.cards[this.cardID];

        for (const m of cardState.modifiers) {
            const behavior = new ModifierTypeMap[m.type].behavior(m, this.cardID);
            behavior.fillFlipDelays(cardsState, delayByCardID);
        }

        return delayByCardID;
    }

    protected addMatchTaskToQueue(state: IGameState, taskData: Partial<ICardStateTaskData>): IGameState {
        let cardsState = CardStateHelper.getCardsState(state);

            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.MATCH,
                cards: [this.cardID],
                ...taskData
            });

            return CardStateHelper.updateGameState(state, cardsState);
    }
}
